import { VOMelodyPart } from "../model/DataStructure.js";
import { Assets } from "./Assets.js";

export interface MelodyProgressOptions {
    container: HTMLElement;
    visible?: boolean;
    showCurly: boolean;
}

interface MelodyProgressSubtrack {
    part: VOMelodyPart;
    element: HTMLDivElement;
    progress: HTMLDivElement;
}

interface MelodyProgressDot {
    part?: VOMelodyPart;
    element: HTMLDivElement;
    index: number;
}

export class MelodyProgress {
    private _options: MelodyProgressOptions;
    private _element: HTMLElement;
    private _parts: VOMelodyPart[] = [];
    private _totalPartCount = 0;


    private _dots: MelodyProgressDot[] = [];
    private _subtracks: MelodyProgressSubtrack[] = [];

    constructor(options: MelodyProgressOptions) {
        options = e5.object.merge({
            visible: true,
            showCurly: false
        }, options);
        this._options = options;

        this._element = document.createElement("div");
        this._element.className = "melody-progress";
        this._element.innerHTML = `
        <div class="melody-progress__left">
            <div class="melody-progress__left-icon e5 icon-590"></div>
        </div>
        <div class="melody-progress__right">
            <div class="melody-progress__track">
                <div class="melody-progress__line-container">
                    <div class="melody-progress__line melody-progress__line--left">${Assets.MELODY_PROGRESS_TRACK}</div>
                    <div class="melody-progress__line melody-progress__line--right">${Assets.MELODY_PROGRESS_TRACK}</div>
                </div>
                <div class="melody-progress__subtracks"></div>
                <div class="melody-progress__dots"></div>
            </div>
            <div class="melody-progress__curly melody-progress__curly--hidden ${options.showCurly ? "" : "e5-hidden"}">
                <div class="melody-progress__curly-fill">
                    <div class="melody-progress__curly-label"></div>
                </div>
            </div>
        </div>        
        `;

        this.visible = options.visible;

        if (options.container) {
            options.container.appendChild(this._element);
        }
    }

    public setPoints(...points: number[]): void {
        this._dots.forEach((d) => {
            const labeled = points.length > 0 && points.indexOf(d.index) !== -1;
            d.element.classList.toggle("melody-progress__dot--labeled", labeled);
        });
    }

    public setCurly(count: number = 0): void {

        if (count === 0) {
            this._element.querySelector(".melody-progress__curly").classList.add("melody-progress__curly--hidden");
            return;
        }

        const total = this._parts.length;
        count = e5.Calc.clamp(count, 1, total)
        const curly = this._element.querySelector(".melody-progress__curly") as HTMLElement;
        curly.style.width = ((count / this._parts.length) * 100).toFixed(8) + "%";

        const label = this._element.querySelector(".melody-progress__curly-label") as HTMLElement;
        // if (count === total) {
        //     label.textContent = "Alle Teile";
        // }
        // else if (count === 1) {
        //     label.textContent = "Teil 1";
        // } else {
        //     label.textContent = "Teil 1 bis " + count;
        // }
        if (count === 1) {
            label.textContent = "Teil 1";
        } else {
            label.textContent = "Teil 1 bis " + count;
        }

        this._element.querySelector(".melody-progress__curly").classList.remove("melody-progress__curly--hidden");
    }

    public setProgress(count: number): void {
        count = e5.Calc.clamp(count, 0, this._totalPartCount);


        for (let dot of this._dots) {
            if (!dot.part) {
                dot.element.classList.add("melody-progress__dot--active");
                continue;
            }
            dot.element.classList.toggle("melody-progress__dot--active", dot.part.countTotal <= count);
        }

        var totalPerc = 0;
        for (let subtrack of this._subtracks) {

            const part = subtrack.part;

            // let isActive = part.countTotal <= count;
            let start = part.countTotal - part.count;

            let perc = 0;
            if (count > part.countTotal) {
                perc = 1;
            } else if (count < start) {
                perc = 0;
            } else {
                perc = (count - start) / part.count
            }

            perc = e5.Calc.clamp(perc, 0, 1);
            totalPerc += perc;
            // subtrack.progress.style.width = (perc * 100).toFixed(8) + "%";
        }

        const lineLeft = this._element.querySelector(".melody-progress__line--left") as HTMLElement;
        const lineRight = this._element.querySelector(".melody-progress__line--right") as HTMLElement;
        if (this._subtracks.length === 0) {
            // lineLeft.style.right = "100%";
            // lineRight.style.left = "0";

            lineLeft.style.clipPath = `inset(0 ${100}% 0 0)`;
            lineRight.style.clipPath = `inset(0 0 0 0)`;
        } else {
            totalPerc /= this._subtracks.length;
            // lineLeft.style.right = ((1 - totalPerc) * 100).toFixed(8) + "%";
            // lineRight.style.left = (totalPerc * 100).toFixed(8) + "%";

            lineLeft.style.clipPath = `inset(0 ${((1 - totalPerc) * 100).toFixed(8)}% 0 0)`;
            lineRight.style.clipPath = `inset(0 0 0 ${(totalPerc * 100).toFixed(8)}%)`;
        }
    }

    public get parts(): VOMelodyPart[] {
        return this._parts;
    }

    public set parts(value: VOMelodyPart[]) {
        this._parts = value;

        this._totalPartCount = 0;
        for (var part of this._parts) {
            this._totalPartCount += part.count;
        }


        const dots: string[] = [
            Assets.MELODY_PROGRESS_DOT_1,
            Assets.MELODY_PROGRESS_DOT_2,
            Assets.MELODY_PROGRESS_DOT_3,
            Assets.MELODY_PROGRESS_DOT_4,
            Assets.MELODY_PROGRESS_DOT_5
        ];

        let dotsHtml = '';
        for (var i = 0; i <= value.length; ++i) {
            const left = ((i / value.length) * 100).toFixed(8) + "%";
            let dotSvg = "";
            let extraClass = "";
            if (i === value.length) {
                dotSvg = Assets.MELODY_PROGRESS_END_NOTE;
                extraClass = "melody-progress__dot--end";
            }
            else {
                if (i === 0) {
                    extraClass = "melody-progress__dot--start";
                }
                dotSvg = dots[i % dots.length];
            }
            dotsHtml += `
            <div class="melody-progress__dot ${extraClass}" data-dot="${i}" data-part="${i - 1}" style="left: ${left};">
                <div class="melody-progress__dot-svg">${dotSvg}</div>
                <div class="melody-progress__dot-label">${i + 1}</div>
            </div>`;
        }
        var track = this._element.querySelector(".melody-progress__dots") as HTMLDivElement;
        track.innerHTML = dotsHtml;

        this._dots = [];
        let index = 0;
        track.querySelectorAll(".melody-progress__dot").forEach((e) => {
            this._dots.push({
                element: e as HTMLDivElement,
                part: this._parts[parseInt(e.getAttribute("data-part"))],
                index: index++ 
            });
        });


        let subtracksHtml = '';
        const subtrackWidth = (100 / value.length).toFixed(8) + "%";
        for (var i = 0; i < value.length; ++i) {
            const left = ((i / value.length) * 100).toFixed(8) + "%";
            subtracksHtml += `<div class="melody-progress__subtrack" data-part="${i}" style="left: ${left}; width: ${subtrackWidth};">
                <div class="melody-progress__subtrack-progress"></div>
            </div>`;
        }
        var subtracks = this._element.querySelector(".melody-progress__subtracks") as HTMLDivElement;
        subtracks.innerHTML = subtracksHtml;

        this._subtracks = [];
        subtracks.querySelectorAll(".melody-progress__subtrack").forEach((e) => {
            this._subtracks.push({
                element: e as HTMLDivElement,
                progress: e.querySelector(".melody-progress__subtrack-progress"),
                part: this._parts[parseInt(e.getAttribute("data-part"))],
            });
        });
    }

    public get visible(): boolean {
        return !this._element.classList.contains("melody-progress--hidden");
    }

    public set visible(value: boolean) {
        this._element.classList.toggle("melody-progress--hidden", !value);
    }
}